// ============================================
// Member Information Section
// Basic member details used in all PDFs
// ============================================

import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSubmission } from '../../types';
import FormField from '../ui/FormField';
import { useLanguage } from '../../i18n/LanguageContext';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const MemberInfoSection: React.FC<{
  onMedicalIdStatusChange?: (exists: boolean) => void;
  isEditMode?: boolean;
  isPatientAccess?: boolean;
  isLocked?: boolean;
}> = ({ onMedicalIdStatusChange, isEditMode, isPatientAccess, isLocked }) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';
  const { register, formState: { errors }, watch, setError, clearErrors } = useFormContext<FormSubmission>();
  const { t } = useLanguage();
  
  const [medicalIdStatus, setMedicalIdStatus] = useState<{
    checking: boolean;
    exists: boolean | null;
  }>({ checking: false, exists: null });

  const medicalId = watch('memberInfo.medicalId');

  useEffect(() => {
    // Skip duplicate check in edit mode or patient access — the medical ID belongs to this submission
    if (isEditMode || isPatientAccess || isLocked) {
      setMedicalIdStatus({ checking: false, exists: null });
      clearErrors('memberInfo.medicalId');
      onMedicalIdStatusChange?.(false);
      return;
    }

    // Debounce check
    if (!medicalId || medicalId.trim() === '' || medicalId.length < 3) {
      setMedicalIdStatus({ checking: false, exists: null });
      clearErrors('memberInfo.medicalId');
      onMedicalIdStatusChange?.(false);
      return;
    }

    setMedicalIdStatus({ checking: true, exists: null });
    
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/forms/check-medical-id/${encodeURIComponent(medicalId)}`);
        const data = await response.json();
        
        setMedicalIdStatus({ checking: false, exists: data.exists });
        onMedicalIdStatusChange?.(data.exists);
        
        if (data.exists) {
          setError('memberInfo.medicalId', {
            type: 'manual',
            message: t('memberInfo.medicalIdExists')
          });
        } else {
          clearErrors('memberInfo.medicalId');
        }
      } catch (error) {
        console.error('Error checking medical ID:', error);
        setMedicalIdStatus({ checking: false, exists: null });
        onMedicalIdStatusChange?.(false);
      }
    }, 800); // 800ms debounce

    return () => clearTimeout(timer);
  }, [medicalId, setError, clearErrors, onMedicalIdStatusChange, t, isEditMode, isPatientAccess, isLocked, API_BASE_URL]);

  const readOnly = !!isPatientAccess || !!isLocked;
  const disabledClass = readOnly ? 'bg-gray-100 cursor-not-allowed' : '';

  return (
    <div className="space-y-6">
      {readOnly && (
        <p className="text-sm text-amber-700 bg-amber-50 p-4 rounded-lg border border-amber-200">
          {isPatientAccess ? t('memberInfo.readOnlyNotice') : 'Member information is locked because this Care Plan will be added to an existing submission.'}
        </p>
      )}
      <p className="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
        {t('memberInfo.pdfNotice')}
      </p>

      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label={t('memberInfo.firstName')}
          error={errors.memberInfo?.firstName?.message}
          required
        >
          <input
            type="text"
            {...register('memberInfo.firstName', { required: t('memberInfo.firstNameRequired') })}
            disabled={readOnly}
            className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
            placeholder={t('memberInfo.firstNamePlaceholder')}
          />
        </FormField>

        <FormField
          label={t('memberInfo.lastName')}
          error={errors.memberInfo?.lastName?.message}
          required
        >
          <input
            type="text"
            {...register('memberInfo.lastName', { required: t('memberInfo.lastNameRequired') })}
            disabled={readOnly}
            className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
            placeholder={t('memberInfo.lastNamePlaceholder')}
          />
        </FormField>
      </div>

      {/* DOB and Medi-Cal ID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label={t('memberInfo.dateOfBirth')}
          error={errors.memberInfo?.dateOfBirth?.message}
          required
        >
          <input
            type="date"
            min="1900-01-01"
            {...register('memberInfo.dateOfBirth', { required: t('memberInfo.dateOfBirthRequired') })}
            disabled={readOnly}
            className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
          />
        </FormField>

        <FormField
          label={t('memberInfo.medicalId')}
          error={errors.memberInfo?.medicalId?.message}
          required
        >
          <div className="relative">
            <input
              type="text"
              {...register('memberInfo.medicalId', {
                required: t('memberInfo.medicalIdRequired'),
                pattern: {
                  value: /^[A-Za-z0-9]+$/,
                  message: t('memberInfo.medicalIdInvalid')
                }
              })}
              disabled={readOnly}
              className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10 ${
                readOnly ? disabledClass :
                medicalIdStatus.exists ? 'border-red-500 focus:border-red-500 focus:ring-red-500' :
                medicalIdStatus.exists === false ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : ''
              }`}
              placeholder={t('memberInfo.medicalIdPlaceholder')}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {medicalIdStatus.checking && (
                <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {!medicalIdStatus.checking && medicalIdStatus.exists === false && (
                <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {!medicalIdStatus.checking && medicalIdStatus.exists === true && (
                <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
          </div>
          {medicalIdStatus.exists && (
            <p className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
              {t('memberInfo.medicalIdExistsWarning')}
            </p>
          )}
        </FormField>
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label={t('memberInfo.phone')}
          error={errors.memberInfo?.phone?.message}
        >
          <input
            type="tel"
            {...register('memberInfo.phone', {
              pattern: {
                value: /^\d{10}$/,
                message: t('memberInfo.phoneInvalid')
              }
            })}
            disabled={readOnly}
            inputMode="numeric"
            maxLength={10}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              target.value = target.value.replace(/\D/g, '').slice(0, 10);
            }}
            className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
            placeholder="5551234567"
          />
        </FormField>

        <FormField
          label={t('memberInfo.email')}
          error={errors.memberInfo?.email?.message}
        >
          <input
            type="email"
            {...register('memberInfo.email', {
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('memberInfo.emailInvalid')
              }
            })}
            disabled={readOnly}
            className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
            placeholder={t('memberInfo.emailPlaceholder')}
          />
        </FormField>
      </div>

      {/* Address */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('memberInfo.addressHeading')}</h3>

        <FormField
          label={t('memberInfo.street')}
          error={errors.memberInfo?.address?.street?.message}
          required
        >
          <input
            type="text"
            {...register('memberInfo.address.street', { required: t('memberInfo.streetRequired') })}
            disabled={readOnly}
            className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
            placeholder={t('memberInfo.streetPlaceholder')}
          />
        </FormField>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div className="col-span-2">
            <FormField
              label={t('memberInfo.city')}
              error={errors.memberInfo?.address?.city?.message}
              required
            >
              <input
                type="text"
                {...register('memberInfo.address.city', { required: t('memberInfo.cityRequired') })}
                disabled={readOnly}
                className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
                placeholder={t('memberInfo.cityPlaceholder')}
              />
            </FormField>
          </div>

          <FormField
            label={t('memberInfo.state')}
            error={errors.memberInfo?.address?.state?.message}
            required
          >
            <select
              {...register('memberInfo.address.state', { required: t('memberInfo.stateRequired') })}
              disabled={readOnly}
              className={`form-select w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
            >
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </FormField>

          <FormField
            label={t('memberInfo.zipCode')}
            error={errors.memberInfo?.address?.zipCode?.message}
            required
          >
            <input
              type="text"
              {...register('memberInfo.address.zipCode', {
                required: t('memberInfo.zipCodeRequired'),
                pattern: {
                  value: /^\d{5}(-\d{4})?$/,
                  message: t('memberInfo.zipCodeInvalid')
                }
              })}
              disabled={readOnly}
              inputMode="numeric"
              maxLength={10}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                let value = target.value.replace(/[^\d-]/g, '');
                value = value.replace(/-+/g, '-');
                value = value.replace(/(?!^)-/g, '');
                if (value.length > 5) {
                  const digits = value.replace(/\D/g, '');
                  value = `${digits.slice(0, 5)}${digits.length > 5 ? `-${digits.slice(5, 9)}` : ''}`;
                } else {
                  value = value.replace(/-/g, '');
                }
                target.value = value.slice(0, 10);
              }}
              className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${disabledClass}`}
              placeholder="90001"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

export default MemberInfoSection;

